import { Box, Typography, Paper } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { AddressAutocompleteField } from '@/shared/components/forms';

/**
 * Location section component for the offer creation form.
 * Allows users to enter the address where the material is located.
 *
 * Features:
 * - Required field with validation
 * - Autocomplete with address suggestions from OpenStreetMap
 * - Whitespace trimming validation
 * - Mobile-optimized input
 * - Accessible with proper ARIA attributes
 */
export function LocationSection() {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
      elevation={0}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            backgroundColor: '#2C5F7C',
            borderRadius: '16px',
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <LocationOnIcon sx={{ color: 'white', fontSize: 22 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          {t('publish.location.title')}
        </Typography>
      </Box>

      <AddressAutocompleteField
        name="address"
        control={control}
        id="address-input"
        label={t('publish.location.addressLabel')}
        placeholder={t('publish.location.placeholder')}
        required
        error={!!errors.address}
        helperText={errors.address?.message as string}
        maxLength={200}
        ariaLabel={t('publish.location.addressLabel')}
        onAddressSelected={suggestion => {
          // Store coordinates for distance calculation in searches
          if (suggestion.coordinates) {
            setValue('latitude', suggestion.coordinates.lat);
            setValue('longitude', suggestion.coordinates.lng);
          }
        }}
        onCoordinatesChange={(lat, lng) => {
          // Update coordinates whether selected or geocoded
          setValue('latitude', lat);
          setValue('longitude', lng);
        }}
        rules={{
          required: t('publish.location.required'),
          validate: (value: string) => {
            if (typeof value === 'string' && value.trim().length === 0) {
              return t('publish.location.required');
            }
            return true;
          },
        }}
      />
    </Paper>
  );
}
